// Mobile navigation: open / close the menu and contain focus while it is open.
//
// This is the theme's ONLY frontend script. It is a progressive enhancement
// and self-guards if its elements are absent. Archive / primary navigation
// never depends on it - the {{navigation}} and {{pagination}} links are plain
// server-rendered HTML that work with JS enabled, disabled, or broken.
//
// Stage 3B added focus containment: while the menu is open, background
// regions (#gh-main, .gh-foot, the skip link) are set `inert` so they are
// neither keyboard- nor assistive-tech-reachable, and Tab / Shift+Tab wrap
// inside the header surface. `inert` is native in current browsers; where it
// is unsupported the Tab-wrap still contains focus and Escape still closes.
export default function menuOpen() {
    const burgerButton = document.querySelector('.gh-burger');

    if (!burgerButton) {
        return;
    }

    const header = document.getElementById('gh-head');
    const menu = document.getElementById(burgerButton.getAttribute('aria-controls'));

    if (!header || !menu) {
        return;
    }

    const backgroundRegions = [
        document.getElementById('gh-main'),
        document.querySelector('.gh-foot'),
        document.querySelector('.gh-skip-link')
    ].filter(Boolean);

    const FOCUSABLE = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    function visibleFocusable() {
        return Array.prototype.filter.call(
            header.querySelectorAll(FOCUSABLE),
            function (el) {
                return el.getClientRects().length > 0;
            }
        );
    }

    function setBackgroundInert(isInert) {
        backgroundRegions.forEach(function (el) {
            el.inert = isInert;
        });
    }

    function isOpen() {
        return burgerButton.getAttribute('aria-expanded') === 'true';
    }

    function setMenuOpen(open) {
        document.body.classList.toggle('gh-head-open', open);
        burgerButton.setAttribute('aria-expanded', String(open));
        burgerButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        setBackgroundInert(open);
    }

    burgerButton.addEventListener('click', function () {
        setMenuOpen(!isOpen());
    });

    document.addEventListener('keydown', function (event) {
        if (!isOpen()) {
            return;
        }

        if (event.key === 'Escape') {
            setMenuOpen(false);
            burgerButton.focus();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const items = visibleFocusable();

        if (!items.length) {
            return;
        }

        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        const outside = !header.contains(active);

        if (event.shiftKey && (active === first || outside)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || outside)) {
            event.preventDefault();
            first.focus();
        }
    });

    // If the viewport crosses the 900px header breakpoint while the menu is
    // open (e.g. device rotation), close it so background regions never stay
    // `inert` on the desktop layout where there is no burger to reopen from.
    const desktop = window.matchMedia('(min-width: 901px)');

    function handleDesktop(event) {
        if (event.matches && isOpen()) {
            setMenuOpen(false);
        }
    }

    if (desktop.addEventListener) {
        desktop.addEventListener('change', handleDesktop);
    } else if (desktop.addListener) {
        desktop.addListener(handleDesktop);
    }

    document.body.classList.add('gh-menu-ready');
}
